import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import { v4 as uuidv4 } from 'uuid';
import { gql } from '@apollo/client';
import MODE from '$components/review/mode';
import logger from '$lib/logger';

const RECORD_PRESENTATIONS_MUTATION = gql`
  mutation RecordPresentations($input: RecordPresentationsInput!) {
    recordPresentations(input: $input) {
      _empty
    }
  }
`;

class PresentationManager {
  constructor({ client, mode, sessionUuid }) {
    this.client = client;
    this.mode = mode;
    this.isExam = mode === MODE.exam;
    this.sessionUuid = sessionUuid;
    this.resetSession();
  }

  resetSession() {
    this.sessionUuid = this.sessionUuid || uuidv4();
    this.seenItemUuids = [];
    this.reset();
  }

  reset() {
    this.current = {};
  }

  stop() {
    this.stopped = true;
  }

  track({ entry, stage = 'question', isCorrect }) {
    if (this.stopped) {
      return;
    }

    logger.debug(`PresentationManager.track({ stage: ${stage} })`);
    switch (stage) {
      case 'question': {
        if (!_isEmpty(this.current)) {
          logger.warn('presentationManager.track() called during dirty session');
          this.reset();
        }

        if (!entry) {
          logger.warn(
            "presentationManager.track({ stage: 'question' }) called without entry"
          );
          return;
        }

        this.current.entry = entry;
        this.current.questionStart = new Date().getTime();
        return;
      }
      case 'answered': {
        if (_isEmpty(this.current)) {
          logger.warn(
            "presentationManager.track({ stage: 'answered' }) called without current"
          );
          return;
        }

        this.current.answered = new Date().getTime();
        this.current.isCorrect = isCorrect;
        return;
      }
      case 'review': {
        if (_isEmpty(this.current)) {
          logger.warn(
            "presentationManager.track({ stage: 'review' }) called without current"
          );
          return;
        }

        this.current.reviewStart = new Date().getTime();
        this.current.isCorrect = isCorrect;
        return;
      }
      default: {
        logger.warn(`presentationManager.track() called with unknown stage: ${stage}`);
      }
    }
  }

  record() {
    if (this.stopped) {
      return;
    }

    logger.debug('PresentationManager.record()');
    if (_isEmpty(this.current)) {
      logger.warn('presentationManager.record() called without current');
    }

    this.current.finished = new Date().getTime();
    const { isCorrect, entry, questionStart, reviewStart, answered, finished } =
      this.current;

    if (this.isExam) {
      if (!questionStart) {
        logger.warn('presentationManager.record() missing timestamps');
        this.reset();
        return;
      }
    } else if (!questionStart || !reviewStart || !finished) {
      logger.warn('presentationManager.record() missing timestamps');
      this.reset();
      return;
    }

    const questionMs = this.isExam
      ? (answered || finished) - questionStart
      : reviewStart - questionStart;
    const reviewMs = this.isExam // eslint-disable-line no-nested-ternary
      ? answered
        ? finished - answered
        : 0
      : finished - reviewStart;
    const totalMs = questionMs + reviewMs;

    const presentation = {
      itemUuid: entry.item.itemUuid || entry.item.uuid,
      isCorrect,
      questionMs,
      reviewMs,
      totalMs,
      seenAt: questionStart,
    };
    this.upload({ presentation });

    this.reset();
  }

  upload({ presentation: rawPresentation }) {
    const { itemUuid } = rawPresentation;
    const isRepeat = _includes(this.seenItemUuids, itemUuid);
    this.seenItemUuids.push(itemUuid);

    const presentation = {
      ...rawPresentation,
      sessionUuid: this.sessionUuid,
      isRepeat,
    };

    // 20181213JP: FEATURE(offline) - Add error handling / retry logic
    this.client.mutate({
      mutation: RECORD_PRESENTATIONS_MUTATION,
      variables: {
        input: {
          presentations: [presentation],
        },
      },
    });
  }
}

export default PresentationManager;
